import React, {useRef} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {borderRadius, p, pl, position} from "@pg-design/helpers-css";
import {prettyScroll} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {ActiveIdType, useActiveElementId} from "@pg-mono/hooks";

import {IRPStore} from "../../../../app/rp_reducer";
import {useScrollListElementIntoView} from "../../../../hooks/useScrollListElementIntoView";
import {ITableOfContentsElement} from "../../../actions/fetch_article_detail";
import {ChapterItem} from "./ChapterItem";

interface ITableOfContentsBlock {
    activeItemId: ActiveIdType;
    elements?: ITableOfContentsElement[];
    id?: string | null;
    text?: string | null;
    isPlacedInModal?: boolean;
    scrollToElementIntoView: (target: HTMLElement) => void;
}

const TableOfContentsBlock = (props: ITableOfContentsBlock) => {
    const {elements = [], id, text, ...rest} = props;

    return (
        <ul>
            {id && <ChapterItem id={id} text={text} {...rest} />}
            {elements.map(({id, ...elementProps}) => (
                <TableOfContentsBlock key={id} id={id} {...elementProps} {...rest} />
            ))}
        </ul>
    );
};

interface IArticleTableOfContents {
    showHeader?: boolean;
    isPlacedInModal?: boolean;
    onTableOfContentsClick?: () => void;
}

export const TableOfContents = (props: IArticleTableOfContents) => {
    const {isPlacedInModal, onTableOfContentsClick, ...rest} = props;
    const article = useSelector((state: IRPStore) => state.article.detail.article);
    const activeItemId = useActiveElementId(article?.article_header_ids as string[], {rootMargin: `0% 0% -85% 0%`, threshold: 0.5});
    const tableOfContentsRef = useRef<HTMLDivElement | null>(null);

    if (!article?.table_of_contents) {
        return null;
    }

    const {table_of_contents, table_of_contents_title} = article;

    const scrollListElementIntoView = useScrollListElementIntoView();

    const scrollToElementIntoView = (target: HTMLElement) => {
        if (tableOfContentsRef.current !== null) {
            scrollListElementIntoView({container: tableOfContentsRef.current, target});
        }
    };

    return (
        <TableOfContentsBase
            ref={tableOfContentsRef}
            css={isPlacedInModal ? null : stickyPanel}
            isPlacedInModal={isPlacedInModal}
            onClick={() => onTableOfContentsClick?.()}
            {...rest}
        >
            <div css={position("relative")}>
                <Text variant="headline_6" as="div">
                    {table_of_contents_title || "Spis treści"}
                </Text>
                <TableOfContentsBlock
                    scrollToElementIntoView={scrollToElementIntoView}
                    elements={table_of_contents}
                    activeItemId={activeItemId}
                    isPlacedInModal={isPlacedInModal}
                />
            </div>
        </TableOfContentsBase>
    );
};

const stickyPanel = css`
    position: sticky;
    top: 1rem;
    max-height: calc(100vh - 4rem);
    ${p(2, 1)};
    background-color: #fff;
    ${borderRadius(2)}
    ${prettyScroll()};
`;

interface ITableOfContents {
    showHeader?: boolean;
    isPlacedInModal?: boolean;
}

const TableOfContentsBase = styled.div<ITableOfContents>`
    margin-top: ${(props) => (props.isPlacedInModal ? 0 : "2.5rem")};

    ul {
        list-style: none;
        margin: ${(props) => (props.isPlacedInModal ? 0 : "1rem 0 0")};
        padding: 0;

        ul > ul {
            ${pl(1)};
        }

        a {
            text-decoration: none;

            line-height: ${(props) => (props.isPlacedInModal ? 4.1 : 2.2)};
            text-transform: initial;

            &:hover {
                color: initial;
            }
        }
    }
`;
